import React from "react";
import api from "@services/api";
import { useAuth } from "@context/AuthContext";
import "./my-deliveries.css";

const todayISO = () => new Date().toISOString().slice(0, 10);

/* Small helper: build a printable delivery manifest in a new window. */
function printManifest({ agentName, date, type, orders }) {
  const rows = orders.map((o, i) => `
    <tr>
      <td class="n">${i + 1}</td>
      <td>#${o.order_id}</td>
      <td>${escapeHtml(o.customer || "")}</td>
      <td>${escapeHtml(o.telephone || "")}</td>
      <td class="addr">${escapeHtml(o.address || "")}</td>
      <td class="amt">${Number(o.total || 0).toFixed(2)}</td>
      <td class="sig"></td>
    </tr>`).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Delivery Sheet</title>
  <style>
    *{font-family:Arial,Helvetica,sans-serif;}
    body{margin:18px;color:#111;}
    h1{font-size:18px;margin:0 0 2px;}
    .meta{font-size:12px;color:#444;margin-bottom:12px;}
    table{width:100%;border-collapse:collapse;font-size:12px;}
    th,td{border:1px solid #999;padding:5px 6px;text-align:left;vertical-align:top;}
    th{background:#f0f0f0;}
    td.n{width:26px;text-align:center;} td.amt{text-align:right;white-space:nowrap;}
    td.addr{max-width:280px;} td.sig{width:90px;}
    tfoot td{font-weight:bold;}
    @media print{ .noprint{display:none;} }
  </style></head><body>
  <h1>Delivery Sheet — ${type === "pickup" ? "Pickups" : "Deliveries"}</h1>
  <div class="meta"><b>Agent:</b> ${escapeHtml(agentName)} &nbsp;·&nbsp; <b>Date:</b> ${date || "—"} &nbsp;·&nbsp; <b>Stops:</b> ${orders.length}</div>
  <table><thead><tr>
    <th>#</th><th>Order</th><th>Customer</th><th>Phone</th><th>Address</th><th>Amount</th><th>Signature</th>
  </tr></thead><tbody>${rows}</tbody>
  <tfoot><tr><td colspan="5" style="text-align:right">Total</td>
    <td class="amt">${orders.reduce((s, o) => s + Number(o.total || 0), 0).toFixed(2)}</td><td></td></tr></tfoot>
  </table>
  <p class="noprint" style="margin-top:16px"><button onclick="window.print()">Print</button></p>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) { alert("Please allow pop-ups to print the delivery sheet."); return; }
  w.document.write(html); w.document.close();
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export default function MyDeliveriesPage() {
  const { user } = useAuth();
  const agentName = [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.username || "Agent";

  const [date, setDate] = React.useState(todayISO());
  const [type, setType] = React.useState("dropoff");
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const dragIdx = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const { data } = await api.get("/assignments/my-orders", { params: { date, type } });
      setOrders(data.orders || []);
      setDirty(false);
    } catch (e) {
      setMsg({ type: "err", text: "Could not load your orders." });
    } finally { setLoading(false); }
  }, [date, type]);

  React.useEffect(() => { load(); }, [load]);

  function move(from, to) {
    if (to < 0 || to >= orders.length) return;
    const next = orders.slice();
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setOrders(next); setDirty(true);
  }

  async function save() {
    setSaving(true); setMsg(null);
    try {
      await api.put("/assignments/my-orders/sort", { type, order_ids: orders.map((o) => o.order_id) });
      setDirty(false);
      setMsg({ type: "ok", text: "Route order saved." });
    } catch (e) {
      setMsg({ type: "err", text: "Could not save the order." });
    } finally { setSaving(false); }
  }

  const totalAmt = orders.reduce((s, o) => s + Number(o.total || 0), 0);

  return (
    <div className="mydel">
      <div className="mydel-head">
        <div>
          <h1>My Deliveries</h1>
          <div className="mydel-sub">{agentName}</div>
        </div>
        <div className="mydel-controls">
          <label>Date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          <label>Type
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="dropoff">Deliveries (drop-off)</option>
              <option value="pickup">Pickups</option>
            </select>
          </label>
          <button className="mydel-btn" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
        </div>
      </div>

      {msg && <div className={`mydel-msg ${msg.type}`}>{msg.text}</div>}

      <div className="mydel-bar">
        <div><b>{orders.length}</b> stop{orders.length === 1 ? "" : "s"} · total <b>{totalAmt.toFixed(2)}</b> JOD</div>
        <div className="mydel-actions">
          <button className="mydel-btn primary" onClick={save} disabled={!dirty || saving}>
            {saving ? "Saving…" : dirty ? "Save route order" : "Saved"}
          </button>
          <button className="mydel-btn" onClick={() => printManifest({ agentName, date, type, orders })} disabled={!orders.length}>
            🖨 Print sheet
          </button>
        </div>
      </div>

      {!orders.length && !loading && <div className="mydel-empty">No orders assigned to you for this date.</div>}

      <ol className="mydel-list">
        {orders.map((o, i) => (
          <li
            key={o.order_id}
            className="mydel-item"
            draggable
            onDragStart={() => { dragIdx.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIdx.current !== null && dragIdx.current !== i) move(dragIdx.current, i); dragIdx.current = null; }}
          >
            <span className="mydel-seq">{i + 1}</span>
            <div className="mydel-body">
              <div className="mydel-row1">
                <a href={`/orders/${o.order_id}`} target="_blank" rel="noreferrer" className="mydel-oid">#{o.order_id}</a>
                <span className="mydel-cust">{o.customer}</span>
                {o.telephone && <a className="mydel-tel" href={`tel:${o.telephone}`}>{o.telephone}</a>}
                <span className="mydel-amt">{Number(o.total || 0).toFixed(2)}</span>
              </div>
              {o.address && <div className="mydel-addr">{o.address}</div>}
            </div>
            <div className="mydel-move">
              <button title="Up" onClick={() => move(i, i - 1)} disabled={i === 0}>▲</button>
              <button title="Down" onClick={() => move(i, i + 1)} disabled={i === orders.length - 1}>▼</button>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
